<template>
  <vx-card class="mt-base" :title="activityTitle">
    <h4 class="mb-5">Make Your Own Polymer: Casein Plastic from Milk</h4>
    <p class="mb-5">
      Now that you know a little about what polymers are and how they are developed, why not have a
      go at making your own!
    </p>
    <p class="mb-5">
      In the following experiment, you’ll learn how to make the well-known polymer called plastic
      using only kitchen ingredients!
    </p>
    <p class="mb-5">
      In this experiment, you’ll first make a ball of soft dough-like material, which you can mould
      into any shape you like. Then, you’ll leave it to dry for a few days, and it will harden like
      plastic.
    </p>

    <h4 class="mb-5">You’ll need:</h4>
    <ul class="default-list mb-5">
      <li>Whole milk</li>
      <li>White vinegar</li>
      <li>2-cup glass measuring cup</li>
      <li>A teaspoon</li>
      <li>A spoon for stirring</li>
      <li>A microwave</li>
      <li>A pot holder</li>
      <li>Paper towels</li>
      <li>A fine mesh strainer</li>
    </ul>

    <h4 class="mb-5">Instructions:</h4>
    <ul class="numbered-list mb-5">
      <li>Make a stack of 5 or 6 layers of paper towels.</li>
      <li>Pour 1 cup of milk into your glass measuring cup.</li>
      <li>
        Microwave the milk for 1 minute. You want it to be heated through but not boiling, heat for
        another 20-30 seconds if needed.
      </li>
      <li>
        Use a potholder to remove the cup. Add 4 teaspoons of vinegar to the milk and stir gently.
        There should be solids floating around in the cup. (Warning: it’s a bit stinky!)
        <centered-image-wrapper
          :image="require('@/assets/images/experiences/engineering/act_11_step4.png')"
          alt="Step 4: a glass measuring cup of warm milk being stirred with a spoon after adding vinegar, with white solids forming in the liquid."
        />
      </li>

      <li>
        Use your spoon to scoop out as many of the solids as you can onto the stack of paper towels.
        <centered-image-wrapper
          :image="require('@/assets/images/experiences/engineering/act_11_step5.png')"
          alt="Step 5: a spoon scooping the white curd solids out of the cup onto a stack of paper towels."
        />
      </li>

      <li>
        Once you have removed most of the solids, carefully strain the liquid through a fine mesh
        strainer into the sink or a bowl.
      </li>
      <li>
        Dump the solids left in the strainer onto the stack of paper towels and gently pat them dry
        (use more paper towels if necessary to soak up as much moisture from the solids as you can).
      </li>
      <li>
        When the solids are fairly dry, gather them together and knead them in your hands or on a
        table into a smooth ball.
      </li>
      <li>
        Once it is smooth, you can mold the pliable dough into shapes. Try rolling it flat and using
        cookie cutters to shape it, press it into play dough molds, form it into beads (use a
        toothpick to make holes), or just shape it with your fingers.
        <centered-image-wrapper
          :image="require('@/assets/images/experiences/engineering/act_11_step9.png')"
          alt="Step 9: a heart-shaped cookie cutter pressed into the casein dough rolled out flat on a table."
        />
      </li>
      <li>
        Set your shapes on a sheet of waxed paper and leave them out to dry for a few days until
        they become hard like plastic. (Note: keep in mind that the shapes will warp slightly and
        possibly even crack as they dry, so don’t expect your shapes to be perfect when they’ve
        hardened.)
      </li>
      <li>
        Remove any remaining solids from your dishes with a paper towel before washing with soap and
        warm water.
      </li>
    </ul>

    <p class="mb-5">
      Take some photos as you work your way through this process and especially when your creation
      has hardened. You can upload these photos here.
    </p>

    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      filetype="photo_doc"
      upload-btn-text="Upload Photo 1"
      index="1"
    />
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_2']"
      :is-editable="isEditable"
      filetype="photo_doc"
      upload-btn-text="Upload Photo 2"
      index="2"
    />
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_3']"
      :is-editable="isEditable"
      filetype="photo_doc"
      upload-btn-text="Upload Photo 3"
      index="3"
    />

    <h4 class="mb-5 mt-5">So, What Happened?</h4>
    <p class="mb-5">
      When you mixed vinegar with milk and heated it, the milk curdled or separated into curds (the
      solids) and whey (the liquid). Curds are the fat and casein (protein) from the milk. They form
      together in chains known as polymers. All plastics are polymers. The casein chains you formed
      in this experiment will harden into a type of plastic. In the early 1900’s, casein plastic
      like this was used to make many plastic objects including buttons and jewellery.
    </p>
    <p class="mb-5">
      [Source:<a
        href="https://learning-center.homesciencetools.com/article/casein-plastic-project/"
        target="_blank"
      >
        https://learning-center.homesciencetools.com/article/casein-plastic-project</a
      >]
    </p>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/engineering/chemical_engineering_splash.jpg'),
          video: 'https://www.youtube.com/embed/aRKyJRAxjpM',
        },
        2: {
          image: require('@/assets/images/experiences/engineering/molecule_video_splash.jpg'),
          video: 'https://www.youtube.com/embed/C0Qaf-UJ2XQ',
        },
        3: {
          image: require('@/assets/images/experiences/engineering/polymers_video_splash.jpg'),
          video: 'https://www.youtube.com/embed/rHxxLYzJ8Sw',
        },
      },
      questions1: {
        1: {
          question: '1. Chemical engineering not only focuses on chemicals but also...',
          answers: [
            {
              val: 1,
              answer: 'a. Developing and designing bridges and other infrastructure',
              correct: false,
            },
            { val: 2, answer: 'b. Developing and designing machines and engines', correct: false },
            {
              val: 3,
              answer:
                'c. Developing and designing plants and processes for manufacturing chemicals',
              correct: true,
            },
          ],
        },
        2: {
          question: '2. Biotechnology engineering applies technology to biological systems and...',
          answers: [
            { val: 1, answer: 'a. Chemical processes', correct: false },
            { val: 2, answer: 'b. Living organisms', correct: true },
            { val: 3, answer: 'c. Polymerization', correct: false },
          ],
        },
        3: {
          question: '3. Biomass refers to:',
          answers: [
            { val: 1, answer: 'a. Renewable organic material', correct: true },
            { val: 2, answer: 'b. Plastics and other recyclables', correct: false },
            { val: 3, answer: 'c. Non-renewable resources', correct: false },
          ],
        },
      },
      questions2: {
        1: {
          question: '1. What is the first element on the periodic table?',
          answer: '',
          correct: 'Hydrogen',
        },
        2: {
          question: '2. What is the centre of an atom called?',
          answer: '',
          correct: 'A nucleus',
        },
        3: {
          question: '3. True or false? Acids have a pH level below 7.',
          answer: '',
          correct: 'True',
        },
        4: {
          question: '4. What is the main gas found in the air we breathe?',
          answer: '',
          correct: 'Nitrogen (around 78%)',
        },
        5: {
          question: '5. True or false? An electron carries a positive charge.',
          answer: '',
          correct: 'False',
        },
        6: {
          question:
            '6. Famous New Zealand scientist Ernest Rutherford was awarded a Nobel Prize in which field?',
          answer: '',
          correct: 'Chemistry',
        },
        7: {
          question: '7. What is the chemical symbol for gold?',
          answer: '',
          correct: 'Au',
        },
        8: {
          question: '8. K is the chemical symbol for which element?',
          answer: '',
          correct: 'Potassium',
        },
        9: {
          question: '9. What orbits the nucleus of an atom?',
          answer: '',
          correct: 'Electrons',
        },
        10: {
          question: '10. At room temperature, what is the only metal that is in liquid form?',
          answer: '',
          correct: 'Mercury',
        },
        11: {
          question: '11. True or false? A neutron has no net electric charge.',
          answer: '',
          correct: 'True',
        },
        12: {
          question:
            '12. A nuclear reaction where the nucleus of an atom splits into smaller parts is known as nuclear fission or nuclear fusion?',
          answer: '',
          correct: 'Nuclear fission',
        },
        13: {
          question: '13. What is H20 more commonly known as?',
          answer: '',
          correct: 'Water',
        },
        14: {
          question: '14. What is the third most common gas found in the air we breathe?',
          answer: '',
          correct: 'Argon (around 1%)',
        },
        15: {
          question:
            '15. What is the name given to substances that are initially involved in a chemical reaction?',
          answer: '',
          correct: 'Reactants',
        },
        16: {
          question: '16. True or false? Bases have a pH level below 7.',
          answer: '',
          correct: 'False',
        },
        17: {
          question: '17. Is sodium hydroxide (NaOH) an acid or base?',
          answer: '',
          correct: 'Base',
        },
        18: {
          question:
            '18. Atoms of the same chemical element that have different atomic mass are known as?',
          answer: '',
          correct: 'Isotopes',
        },
        19: {
          question: '19. True or false? A proton carries a positive charge.',
          answer: '',
          correct: 'True',
        },
        20: {
          question:
            '20. What is the fourth most abundant element in the universe in terms of mass?',
          answer: '',
          correct: 'Carbon',
        },
      },
      popupActive: false,
      question3: '',
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
      },
    };
  },
  methods: {
    ...methods,
    checkAnswers() {
      this.popupActive = true;
    },
  },
  computed: {
    ...computed,
    video() {
      return {
        url: 'https://www.youtube.com/embed/WI0pGk0MMhg',
        image: require('@/assets/images/experiences/engineering/instruction_video_trigger.jpg'),
      };
    },
  },
  inject: inject,
};
</script>
